import { reaction } from "mobx";
import { useEffect, useMemo, useRef } from "react";
import { resolveEcommerceProductById } from "../../../others/ecommerceProducts.js";
import { buildMatomoEventNameWithProductMeta, clearMatomoEcommerceCart, trackAnalyticsEvent, trackMatomoEcommerceCartUpdate, trackMatomoEcommerceItem, trackMatomoEcommerceOrder } from "../../../others/tracking.js";
import { useStores } from "../../../store/stores.js";
import { CheckoutMatomoUiStep, resolveReadyUiStep } from "../utils/matomoUiStep.js";
const trackedMatomoOrderIdsV2 = new Set();
/**
 * Mirrors v1's `useCheckoutStepInvokeGtag` for the v2 store. Fires
 * `addEcommerceItem` + `trackEcommerceCartUpdate` on every cart-revenue
 * change up to and including the Payment step, plus the per-step funnel
 * events (`begin_checkout`, `add_payment_info`, `purchase` + GA-Ads
 * conversion + `trackEcommerceOrder`) at the appropriate transitions.
 *
 * Mount in `root.tsx` AFTER `useCheckoutV2StepInvokeMatomoPageView()` so
 * the `setCustomUrl` page-view push lands in the `_paq` queue first —
 * Matomo associates events with the most recent virtual URL.
 */ function useCheckoutV2StepInvokeGtag() {
    const { checkoutV2Store } = useStores();
    const lastTrackedCartRevenueSignatureRef = useRef();
    const hasTrackedInitialMatomoCartItemRef = useRef(false);
    const hasTrackedBeginCheckoutRef = useRef(false);
    const lastTrackedStepEventSignatureRef = useRef();
    const { hasCheckoutFormInteraction } = checkoutV2Store;
    const currentUiStep = resolveReadyUiStep(checkoutV2Store.step, checkoutV2Store.view, checkoutV2Store.loading);
    // Cart-update channel: tracks Matomo cart state for the Personal-info /
    // Payment buckets. Fires `addEcommerceItem` once (first revenue we see)
    // and `trackEcommerceCartUpdate` on every subsequent change of the
    // signature `uiStep:SKU:revenue`. Confirmation bucket is excluded — the
    // post-purchase order tracking happens in the per-step effect below.
    const dispatchMatomoCartUpdateListener = useMemo(()=>reaction(()=>{
            if (!checkoutV2Store.product || !checkoutV2Store.transaction) {
                return undefined;
            }
            const { product: { data: { productPlan: { price: [{ initialPrice: productPlanInitialPrice }], product: { id: productId, name: productName } } } }, transaction: { initialPrice }, step, view, loading } = checkoutV2Store;
            const productTrackingConfig = resolveEcommerceProductById(productId);
            const productSKU = productTrackingConfig?.productSKU || String(productId);
            const productTrackingName = productTrackingConfig?.productName || productName;
            const productTrackingCategory = productTrackingConfig?.categoryName || "WordPress plugin";
            const cartGrandTotal = typeof initialPrice === "number" ? initialPrice : productPlanInitialPrice;
            return {
                uiStep: resolveReadyUiStep(step, view, loading),
                productSKU,
                productTrackingName,
                productTrackingCategory,
                cartGrandTotal
            };
        }, (value)=>{
            if (!value || !checkoutV2Store.hasCheckoutFormInteraction || !value.uiStep || value.uiStep === CheckoutMatomoUiStep.Confirmation || typeof value.cartGrandTotal !== "number") {
                return;
            }
            const revenueSignature = `${value.uiStep}:${value.productSKU}:${value.cartGrandTotal}`;
            if (lastTrackedCartRevenueSignatureRef.current === revenueSignature) {
                return;
            }
            lastTrackedCartRevenueSignatureRef.current = revenueSignature;
            if (!hasTrackedInitialMatomoCartItemRef.current) {
                trackMatomoEcommerceItem({
                    productSKU: value.productSKU,
                    productName: value.productTrackingName,
                    categoryName: value.productTrackingCategory,
                    price: value.cartGrandTotal
                });
                hasTrackedInitialMatomoCartItemRef.current = true;
            }
            trackMatomoEcommerceCartUpdate(value.cartGrandTotal);
        }, {
            fireImmediately: true
        }), [
        checkoutV2Store,
        hasCheckoutFormInteraction
    ]);
    useEffect(()=>dispatchMatomoCartUpdateListener, [
        dispatchMatomoCartUpdateListener
    ]);
    useEffect(()=>{
        if (!checkoutV2Store.product || !checkoutV2Store.transaction || !currentUiStep) {
            return;
        }
        const { product: { data: { productPlan: { id: productPlanId, price: [{ initialPrice: productPlanInitialPrice }], product: { id: productId, name: productName } } } }, transaction: { initialPrice, initialPriceTax } } = checkoutV2Store;
        const productTrackingConfig = resolveEcommerceProductById(productId);
        const productSKU = productTrackingConfig?.productSKU || String(productId);
        const productTrackingName = productTrackingConfig?.productName || productName;
        const productTrackingCategory = productTrackingConfig?.categoryName || "WordPress plugin";
        const productTrackingMeta = {
            product_id: String(productId),
            product_name: productTrackingName,
            product_plan_id: String(productPlanId)
        };
        const cartGrandTotal = typeof initialPrice === "number" ? initialPrice : productPlanInitialPrice;
        const taxAmount = typeof initialPriceTax === "number" ? initialPriceTax : undefined;
        const subTotal = typeof taxAmount === "number" ? cartGrandTotal - taxAmount : undefined;
        const discountAmount = typeof productPlanInitialPrice === "number" && typeof cartGrandTotal === "number" ? Math.max(productPlanInitialPrice - cartGrandTotal, 0) : undefined;
        // The effect re-runs whenever any deeply-observed field on the store
        // changes, but we only want one dispatch per (uiStep, order-id-or-
        // null) transition. The order-id dedup is additionally enforced by
        // `trackedMatomoOrderIdsV2` for the Confirmation bucket so cross-
        // mount re-entries stay safe too.
        const stepSignature = `${currentUiStep}:${checkoutV2Store.transaction.checkout?.subscription?.checkout?.ecommerceSystemCheckoutId ?? "pending"}`;
        if (lastTrackedStepEventSignatureRef.current === stepSignature) {
            return;
        }
        lastTrackedStepEventSignatureRef.current = stepSignature;
        switch(currentUiStep){
            case CheckoutMatomoUiStep.PersonalInformation:
                if (!hasCheckoutFormInteraction || hasTrackedBeginCheckoutRef.current) {
                    break;
                }
                trackAnalyticsEvent("begin_checkout", {
                    ga: {
                        event_category: "ecommerce",
                        event_label: "Checkout: Personal information (Step 1)",
                        ...productTrackingMeta
                    },
                    matomo: {
                        category: "ecommerce",
                        name: buildMatomoEventNameWithProductMeta("Checkout: Personal information (Step 1)", productTrackingMeta)
                    }
                });
                hasTrackedBeginCheckoutRef.current = true;
                break;
            case CheckoutMatomoUiStep.Payment:
                trackAnalyticsEvent("add_payment_info", {
                    ga: {
                        event_category: "ecommerce",
                        event_label: "Checkout: Payment (Step 2)",
                        ...productTrackingMeta
                    },
                    matomo: {
                        category: "ecommerce",
                        name: buildMatomoEventNameWithProductMeta("Checkout: Payment (Step 2)", productTrackingMeta)
                    }
                });
                break;
            case CheckoutMatomoUiStep.Confirmation:
                {
                    const ecommerceSystemCheckoutId = checkoutV2Store.transaction.checkout?.subscription?.checkout?.ecommerceSystemCheckoutId;
                    const currency = checkoutV2Store.transaction.checkout?.subscription?.checkout?.currency;
                    if (!ecommerceSystemCheckoutId || !currency) {
                        break;
                    }
                    trackAnalyticsEvent("purchase", {
                        ga: {
                            event_category: "ecommerce",
                            event_label: "Checkout: Download (Step 3)",
                            ...productTrackingMeta
                        },
                        matomo: {
                            category: "ecommerce",
                            name: buildMatomoEventNameWithProductMeta("Checkout: Download (Step 3)", productTrackingMeta)
                        }
                    });
                    trackAnalyticsEvent("conversion", {
                        ga: {
                            send_to: "AW-668507196/llxzCKmS6osCELy44r4C",
                            value: cartGrandTotal - (taxAmount ?? 0),
                            currency,
                            transaction_id: ecommerceSystemCheckoutId,
                            ...productTrackingMeta
                        }
                    });
                    if (!trackedMatomoOrderIdsV2.has(ecommerceSystemCheckoutId)) {
                        trackMatomoEcommerceItem({
                            productSKU,
                            productName: productTrackingName,
                            categoryName: productTrackingCategory,
                            price: cartGrandTotal
                        });
                        trackMatomoEcommerceOrder({
                            orderId: ecommerceSystemCheckoutId,
                            grandTotal: cartGrandTotal,
                            subTotal,
                            tax: taxAmount,
                            shipping: 0,
                            discount: discountAmount
                        });
                        clearMatomoEcommerceCart();
                        trackedMatomoOrderIdsV2.add(ecommerceSystemCheckoutId);
                    }
                    break;
                }
            default:
                break;
        }
    }, [
        currentUiStep,
        hasCheckoutFormInteraction
    ]);
}
export { useCheckoutV2StepInvokeGtag };
